import { DatePicker, Flex, Form, Input, Select } from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { FC, useEffect, useMemo } from "react";
import { getNetworkInfoThunk } from "tm-shared/api";
import { Point } from "tm-shared/types";
import {
    isPointChains,
    isPointDistribution,
    validationMessage,
} from "tm-shared/utils";

import { CONTRACT_TARGET_TYPES_OPTIONS } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { ContractFormValues } from "@/types/form";

interface ContractFormFieldsProps {
    editMode: boolean;
    point: Point | null;
    pointLoading: boolean;
}

export const ContractFormFields: FC<ContractFormFieldsProps> = ({
    editMode,
    point,
    pointLoading,
}) => {
    const dispatch = useAppDispatch();

    const regions = useAppSelector((s) => s.user.regions);
    const networks = useAppSelector((s) => s.user.networks);
    const contractTypesList = useAppSelector(
        (s) => s.offline.contractTypesList,
    );

    // --- 1. Fetched network info if missing
    useEffect(() => {
        if (
            point &&
            isPointChains(point) &&
            !networks?.[point.typeContext.networkId]
        ) {
            dispatch(
                getNetworkInfoThunk({ network: point.typeContext.networkId }),
            );
        }
    }, [dispatch, networks, point]);

    // --- 2. Vocabulary for filtering + correct labels
    const targetTypeVocabulary = useMemo<
        Record<string, string | null | undefined>
    >(
        () => ({
            point: point?.id,
            network:
                point && isPointChains(point)
                    ? point.typeContext.networkId
                    : undefined,
            contragent: point?.market.contragent?.id,
            storage:
                point && isPointDistribution(point)
                    ? point.typeContext.storage?.id
                    : undefined,
            area: point?.areaId,
        }),
        [point],
    );

    // --- 3. Target type options with human-readable suffixes
    const availableTargetTypeOptions = useMemo(() => {
        const labelMap: Record<string, string> = {
            contragent: point?.market.contragent?.name ?? "Невідома юр.особа",
            area: regions?.[point?.areaId || ""] ?? "Невідомий регіон",
            storage:
                (point && isPointDistribution(point)
                    ? point.typeContext.storage?.name
                    : undefined) ?? "Невідомий склад",
            network:
                (point && isPointChains(point)
                    ? networks?.[point.typeContext.networkId || ""]
                    : undefined) ?? "Невідома мережа",
        };

        return CONTRACT_TARGET_TYPES_OPTIONS.filter(
            (opt) => targetTypeVocabulary[opt.value],
        ).map((opt) => ({
            value: opt.value,
            label: labelMap[opt.value]
                ? `${opt.label} (${labelMap[opt.value]})`
                : opt.label,
        }));
    }, [targetTypeVocabulary, regions, networks, point]);

    // --- 4. Select options for contract types
    const contractTypeSelectOptions = useMemo(
        () =>
            Object.entries(contractTypesList).map(([value, label]) => ({
                value,
                label,
            })),
        [contractTypesList],
    );

    // --- 5. Shared date props
    const dateValueProps = (v?: string) => ({
        value: v ? dayjs(v) : null,
    });

    const dateFromEvent = (d?: dayjs.Dayjs | null) =>
        d ? d.format("YYYY-MM-DD") : undefined;

    return (
        <>
            <Form.Item<ContractFormValues>
                name="type"
                label="Тип контракту:"
                rules={[{ required: true, message: "Оберіть тип контракту" }]}
            >
                <Select
                    size="large"
                    options={contractTypeSelectOptions}
                    placeholder="Оберіть тип"
                    disabled={editMode}
                />
            </Form.Item>

            <Form.Item<ContractFormValues>
                name="targetType"
                label="Рівень дії:"
                rules={[{ required: true, message: "Оберіть рівень дії" }]}
            >
                <Select
                    size="large"
                    options={availableTargetTypeOptions}
                    placeholder="Оберіть рівень дії"
                    disabled={editMode}
                    loading={pointLoading}
                />
            </Form.Item>

            <Form.Item<ContractFormValues>
                name="name"
                label="Назва контракту"
                validateFirst
                rules={[
                    { required: true, message: "Введіть назву контракту" },
                    { min: 3, message: validationMessage.minLength(3) },
                    { whitespace: true, message: validationMessage.onlySpaces },
                ]}
            >
                <Input size="large" placeholder="Введіть назву контракту" />
            </Form.Item>

            <Form.Item<ContractFormValues>
                name="description"
                label="Опис"
                style={{ marginBottom: 16 }}
            >
                <TextArea placeholder="Поле для введення" />
            </Form.Item>

            <Flex gap="16px" justify="center" align="center">
                <Form.Item<ContractFormValues>
                    name="startDate"
                    label="Дата початку:"
                    rules={[
                        { required: true, message: "Оберіть дату початку" },
                    ]}
                    getValueProps={dateValueProps}
                    getValueFromEvent={dateFromEvent}
                    style={{ width: "100%" }}
                >
                    <DatePicker size="large" placeholder="Обрати дату" />
                </Form.Item>

                <Form.Item<ContractFormValues>
                    name="endDate"
                    label="Дата завершення:"
                    getValueProps={dateValueProps}
                    getValueFromEvent={dateFromEvent}
                    style={{ width: "100%" }}
                >
                    <DatePicker size="large" placeholder="Обрати дату" />
                </Form.Item>
            </Flex>
        </>
    );
};
